import { useEffect, useState } from "react";
import { Cpu, Sparkles } from "lucide-react";
import { listAgents } from "@/api/entities";
import { Panel, Label, Heading, Loading, Pill, accent } from "@/components/hud";

const DIVISIONS = ["Sales", "Delivery", "Ops", "Content"];
const DIV_TONE = { Sales: "cyan", Delivery: "mint", Ops: "amber", Content: "magenta" };

export default function Crew() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listAgents().then((d) => setAgents(d.items || d || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading label="Waking the crew…" />;

  return (
    <div className="space-y-6 yb-rise" data-testid="crew-page">
      <div>
        <Label>Crew · 18 specialised agents</Label>
        <h1 className="font-display text-[26px] sm:text-[32px] mt-1.5" style={{ color: "var(--yb-text)" }}>
          The Orchestra
        </h1>
        <p className="text-[13px] mt-2 max-w-2xl" style={{ color: "var(--yb-text-muted)" }}>
          Each agent is a supervised workflow specialist. They draft and prepare — a human
          operator approves before anything runs. They never move funds.
        </p>
      </div>

      {DIVISIONS.map((div) => {
        const members = agents.filter((a) => a.division === div);
        const tone = DIV_TONE[div];
        return (
          <section key={div}>
            <Heading
              title={`${div} Division`}
              kicker={`${members.length} agents`}
              accent={tone}
              right={<Pill tone={tone}>{members.filter((m) => m.status === "engaged").length} engaged</Pill>}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {members.map((a) => (
                <AgentCard key={a.id} a={a} />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function AgentCard({ a }) {
  const { c } = accent(a.color);
  const engaged = a.status === "engaged";
  return (
    <Panel className="flex flex-col gap-3">
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded flex items-center justify-center shrink-0"
              style={{ border: `1px solid ${c}`, color: c, boxShadow: engaged ? `0 0 20px -6px ${c}` : "none" }}>
          <Cpu size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-head text-[15px]" style={{ color: "var(--yb-text)" }}>{a.name}</div>
          <div className="font-mono text-[10px] uppercase tracking-wider" style={{ color: "var(--yb-text-muted)" }}>
            {a.division}
          </div>
        </div>
        <span className="dot" style={{ color: engaged ? c : "var(--yb-text-muted)" }} />
      </div>
      <p className="text-[12.5px] leading-relaxed" style={{ color: "var(--yb-text-dim)" }}>{a.role}</p>
      <div className="flex items-center justify-between pt-2 mt-auto" style={{ borderTop: "1px solid var(--yb-hairline)" }}>
        <span className="font-mono text-[11px]" style={{ color: "var(--yb-text-muted)" }}>
          {a.jobs_active} active · {a.jobs_total} total
        </span>
        <span className="font-mono text-[10px] uppercase tracking-wider"
              style={{ color: engaged ? "var(--yb-mint)" : "var(--yb-text-muted)" }}>
          {engaged ? <><Sparkles size={11} className="inline mr-1" />engaged</> : "standby"}
        </span>
      </div>
    </Panel>
  );
}
